import classNames from 'classnames';
import { default as DOMPurify } from 'dompurify';
import { marked } from 'marked';
import { useMemo } from 'react';

import { StringUtils } from '@/common/utils';

const renderer = new marked.Renderer();
renderer.link = ({ href, text }) => `<a target="_blank" href="${href}" title="${href}">${text}</a>`;

interface Props {
  text: string | null | undefined;
  useMarkdown: boolean | undefined;
  /** A single new line becomes a line break (GitHub style). Off by default: other screens render their text without. */
  lineBreaks?: boolean;
  plainTextClassName?: string;
  markdownTextClassName?: string;
}

export const MarkdownPreview = ({
  text,
  useMarkdown,
  lineBreaks = false,
  plainTextClassName,
  markdownTextClassName,
}: Props) => {
  const markdownHTML = useMemo(
    () =>
      useMarkdown && text
        ? DOMPurify.sanitize(marked.parse(text, { gfm: true, breaks: lineBreaks, async: false, renderer }), {
            ADD_ATTR: ['target'],
          })
        : '',
    [text, useMarkdown, lineBreaks],
  );

  if (!useMarkdown) {
    return (
      <div
        dangerouslySetInnerHTML={{ __html: StringUtils.toHTMLAnchor(text || '') }}
        className={classNames('whitespace-pre-line', plainTextClassName)}
      />
    );
  }

  return (
    <div className={markdownTextClassName}>
      <div
        dangerouslySetInnerHTML={{
          __html: markdownHTML,
        }}
        className="min-h-[29px]
          overflow-auto
          [&_*+h1]:!mt-4
          [&_*+h2]:!mt-3
          [&_*+h3]:!mt-2
          [&_*+h4]:!mt-1
          [&_*+h5]:!mt-1
          [&_*+h6]:!mt-1
          [&_a]:break-all
          [&_a]:!text-blue-6
          [&_a]:!underline
          [&_blockquote]:!border-l-4
          [&_blockquote]:!border-l-gray-2
          [&_blockquote]:!pl-2
          [&_code]:!bg-gray-2
          [&_code]:!px-1
          [&_h1]:!text-2xl
          [&_h2]:!text-xl
          [&_h3]:!text-lg
          [&_h4]:!text-base
          [&_h5]:!text-sm
          [&_h6]:!text-xs
          [&_hr]:!mb-2
          [&_hr]:!mt-2
          [&_li>ol]:!py-0
          [&_li>ul]:!py-0
          [&_ol]:!ml-4
          [&_ol]:!list-decimal
          [&_ol]:!py-1
          [&_p]:!my-1
          [&_p]:!leading-[1.5]
          [&_pre>code]:!bg-transparent
          [&_pre>code]:!p-0
          [&_pre]:!my-1
          [&_pre]:!overflow-auto
          [&_pre]:!bg-gray-2
          [&_pre]:!p-2
          [&_td]:!border
          [&_td]:!p-1
          [&_th]:!border
          [&_th]:!p-1
          [&_ul]:!ml-4
          [&_ul]:!list-disc
          [&_ul]:!py-1"
      />
    </div>
  );
};
